import { supabase, isSupabaseConfigured } from "./supabase";

// Maps a Supabase `clients` row into the shape the rest of the app expects.
const rowToClient = (row) => ({
  id:                row.id,
  email:             row.email,
  name:              row.name,
  company:           row.company,
  plan:              row.plan,
  joinDate:          row.join_date,
  stripeLink:        row.stripe_link || import.meta.env.VITE_STRIPE_GROWTH,
  stripeCustomerId:  row.stripe_customer_id,
  strategyNotes:     row.strategy_notes,
  strategyUpdatedAt: row.strategy_updated_at,
  // Phase 4 (2026-06-14): generated column from clients table -- true when
  // the client has filled product_catalog / audience_profile / content_pillars
  // / voice_constraints / cta_posture in their My Brand tab. The portal banner
  // and the pipeline cron both gate on this.
  brand_setup_complete: !!row.brand_setup_complete,
});

const signInSupabase = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });
  if (error) {
    const msg = error.message || "";
    if (/invalid login credentials/i.test(msg)) {
      throw new Error("Incorrect email or password.");
    }
    if (error.status === 429 || /rate.?limit|too many requests/i.test(msg)) {
      throw new Error("Too many login attempts. Please wait a minute, then try again.");
    }
    if (/email.+not.+confirmed/i.test(msg)) {
      throw new Error("Please confirm your email address first — check your inbox for the verification link.");
    }
    if (/network/i.test(msg) || /failed to fetch/i.test(msg)) {
      throw new Error("Couldn't reach the server. Check your internet connection and try again.");
    }
    throw new Error(msg || "Login failed. Please try again.");
  }

  // Check if MFA verification is needed before letting them in.
  const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aalData && aalData.nextLevel === "aal2" && aalData.currentLevel !== "aal2") {
    const { data: factorsData } = await supabase.auth.mfa.listFactors();
    const totpFactor = factorsData?.totp?.find((f) => f.status === "verified");
    if (totpFactor) {
      return { mfaRequired: true, factorId: totpFactor.id };
    }
  }

  return finalizeLogin(data.user);
};

// Builds the post-login result based on the user's app_metadata role.
// Called both after a plain password login (no MFA) and after MFA verification.
const finalizeLogin = async (user) => {
  const role = user?.app_metadata?.role || user?.user_metadata?.role;
  if (role === "owner") return { role: "owner" };

  const { data: row, error: profileErr } = await supabase
    .from("clients")
    .select("*")
    .eq("user_id", user.id)
    .maybeSingle();
  if (profileErr) throw new Error(profileErr.message);
  if (!row) {
    await supabase.auth.signOut().catch(() => {});
    throw new Error("No client profile is linked to this account. Please contact support.");
  }
  return { role: "client", client: rowToClient(row) };
};

// Verifies a TOTP code from the user's authenticator app to complete login.
// Returns the same shape as a successful password-only login.
export const verifyMfaLogin = async (factorId, code) => {
  if (!isSupabaseConfigured()) throw new Error("Authentication is not configured.");
  const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
  if (chErr) throw new Error(chErr.message || "Could not start verification.");
  const { error: vErr } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code: code.trim(),
  });
  if (vErr) {
    if (/invalid.+code|verification.+failed/i.test(vErr.message || "")) {
      throw new Error("That code didn't match. Try again with a fresh 6-digit code from your authenticator app.");
    }
    throw new Error(vErr.message || "Verification failed.");
  }
  const { data: userData } = await supabase.auth.getUser();
  return finalizeLogin(userData.user);
};

// ── MFA enrollment helpers (used by the Security panel) ──────────────────────

export const getMfaStatus = async () => {
  if (!isSupabaseConfigured()) return { enrolled: false };
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw new Error(error.message);
  const verified = data?.totp?.find((f) => f.status === "verified");
  const unverified = data?.totp?.find((f) => f.status === "unverified");
  return {
    enrolled: !!verified,
    verifiedFactorId: verified?.id || null,
    unverifiedFactorId: unverified?.id || null,
  };
};

// Starts an enrollment — returns the QR code (data URI) and the manual-entry secret.
export const enrollMfa = async () => {
  if (!isSupabaseConfigured()) throw new Error("Authentication is not configured.");
  // Clean up any leftover unverified factor from an aborted previous attempt.
  const status = await getMfaStatus();
  if (status.unverifiedFactorId) {
    await supabase.auth.mfa.unenroll({ factorId: status.unverifiedFactorId }).catch(() => {});
  }
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    friendlyName: "MOAP " + new Date().toISOString().split("T")[0],
  });
  if (error) throw new Error(error.message || "Could not start MFA enrollment.");
  return {
    factorId: data.id,
    qrCode: data.totp?.qr_code, // SVG data URI
    secret: data.totp?.secret,   // for manual entry in the authenticator app
    uri: data.totp?.uri,         // otpauth:// URI
  };
};

// Verifies the user's first 6-digit code and marks the factor as verified.
export const verifyMfaEnrollment = async (factorId, code) => {
  if (!isSupabaseConfigured()) throw new Error("Authentication is not configured.");
  const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
  if (chErr) throw new Error(chErr.message || "Could not start verification.");
  const { error: vErr } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: challenge.id,
    code: code.trim(),
  });
  if (vErr) {
    if (/invalid.+code|verification.+failed/i.test(vErr.message || "")) {
      throw new Error("That code didn't match. Make sure your phone's time is correct and try again.");
    }
    throw new Error(vErr.message || "Verification failed.");
  }
  return true;
};

// Removes a verified TOTP factor (turns off 2FA for the account).
export const disableMfa = async (factorId) => {
  if (!isSupabaseConfigured()) throw new Error("Authentication is not configured.");
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  if (error) throw new Error(error.message || "Could not disable 2FA.");
  return true;
};

// Returns { role: "owner" } or { role: "client", client } on success.
// Throws Error with a user-friendly message on failure.
export const signIn = async (email, password) => {
  if (!isSupabaseConfigured()) {
    throw new Error(
      "Authentication is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY."
    );
  }
  return signInSupabase(email, password);
};

// Restore an existing Supabase session on app mount, so a browser refresh
// doesn't kick the user out (their JWT is sitting in localStorage waiting to
// be picked up). Returns the same shape as signIn() on success, or null if no
// active session. Never throws — failures are logged and treated as "no session"
// so a transient Supabase outage doesn't lock the app at boot.
export const restoreSession = async () => {
  if (!isSupabaseConfigured()) return null;
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) { console.warn("getSession error:", error); return null; }
    if (!session?.user) return null;
    return await finalizeLogin(session.user);
  } catch (err) {
    console.warn("restoreSession error:", err);
    return null;
  }
};

export const signOut = async () => {
  if (isSupabaseConfigured()) {
    await supabase.auth.signOut().catch(() => {});
  }
};

// Sends a password-reset email via Supabase Auth. The reset link points to
// /reset-password on this site; that page handles setting a new password.
export const requestPasswordReset = async (email) => {
  if (!isSupabaseConfigured()) {
    throw new Error("Password reset is not available in this environment.");
  }
  const redirectTo =
    (typeof window !== "undefined" ? window.location.origin : "") + "/reset-password";
  const { error } = await supabase.auth.resetPasswordForEmail(
    email.trim().toLowerCase(),
    { redirectTo }
  );
  if (error) {
    if (error.status === 429 || /rate.?limit|too many requests/i.test(error.message || "")) {
      throw new Error("Too many reset attempts. Please wait a minute and try again.");
    }
    throw new Error(error.message || "Could not send reset email. Please try again.");
  }
  return true;
};
